/**
 * Phone numbers for the SMS channel (SMS channel wave).
 *
 * MIRROR: main-consumer `src/lib/phone-e164.ts` (normalisation + allow-list +
 * mobile ranges — the sender's verdict is the binding one), main-backend
 * `SmsPhone` (the verified-number POST) and `SmsMasking.maskE164`,
 * main-consumer's masking helper (same masked output in all). Change one,
 * change all; the shapes are pinned by `smsPhone.test.ts` and, against the
 * canonical cases every copy shares, `smsPhone.parity.test.ts`
 * (`src/lib/__fixtures__/phone-e164-parity.json`, 35 cases with `mobile`).
 */

/** The country a number written without one ("0171 …") belongs to. */
export const SMS_DEFAULT_COUNTRY = "+49";

/** Calling codes the channel may send to — DE, AT, CH (decision 5). */
export const SMS_ALLOWED_PREFIXES: readonly string[] = ["+49", "+43", "+41"];

const E164 = /^\+[1-9]\d{6,14}$/;

/** One trunk zero right after a DE / AT / CH calling code (`+490…`). */
const TRUNK_ZERO = /^\+(49|43|41)0/;

/**
 * "0171 234-5678" → "+491712345678", in the sender's order: a bracketed trunk
 * zero `(0)` goes first (`+49 (0)171 …` — the commonest German way to write a
 * number; stripping the brackets alone left a dead `+4901712345678`), then
 * `( ) - . /` and spaces, `00` → `+`, a leading `0` → the default country, and
 * a single trunk `0` right after `+49` / `+43` / `+41` (`+49 0171 …`) is dropped
 * (audit r1 P2 / S7). `null` when the result is not E.164. Pinned against the
 * canonical `phone-e164-parity.json` (`smsPhone.parity.test.ts`).
 */
export function normalizeE164(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  let s = raw.replace(/\(0\)/g, "").replace(/[()\-./\s]/g, "");
  if (s === "") return null;
  if (s.startsWith("00")) s = "+" + s.slice(2);
  else if (s.startsWith("0")) s = SMS_DEFAULT_COUNTRY + s.slice(1);
  s = s.replace(TRUNK_ZERO, "+$1");
  return E164.test(s) ? s : null;
}

/** Is this E.164 number in an allowed country? */
export function isAllowedSmsCountry(e164: string): boolean {
  return SMS_ALLOWED_PREFIXES.some((p) => e164.startsWith(p));
}

/**
 * The mobile ranges of DE / AT / CH (wave D, B3 — SMS go to mobile numbers
 * only), matched on the E.164 form:
 *  - DE (BNetzA): (0)15…, (0)160, (0)162, (0)163, (0)17… — the 016x block
 *    without paging / trunked radio 0164 / 0167 / 0168 / 0169 and the
 *    unassigned 0161 / 0165 / 0166;
 *  - AT (RTR KEM-V): 650–653, 655, 657, 659–661, 663–699 (0662 is Salzburg's
 *    landline, 0654 / 0656 / 0658 no mobile code);
 *  - CH: 075–079.
 * MIRROR: main-consumer `src/lib/phone-e164.ts`, main-backend `SmsPhone`.
 */
const MOBILE_E164 = /^\+(?:49(?:15|16[023]|17)|43(?:65[0-3579]|66[013-9]|6[7-9]\d)|41(?:7[5-9]))\d/;

/** Is this E.164 number in a DE / AT / CH mobile range? Any other country is not. */
export function isMobileE164(e164: string): boolean {
  return typeof e164 === "string" && MOBILE_E164.test(e164);
}

/** May the SMS channel send to this E.164 number: an allowed country AND a mobile range. */
export function isAllowedSmsNumber(e164: string): boolean {
  return isAllowedSmsCountry(e164) && isMobileE164(e164);
}

/**
 * `+491712345678` → `+49171*****78`: the first 6 characters, one `*` per hidden
 * character, the last 2. A value shorter than 9 characters keeps only its first
 * 3 (`+49***`). Never throws; an empty value stays empty.
 */
export function maskE164(value: string | null | undefined): string {
  if (typeof value !== "string" || value === "") return "";
  if (value.length < 9) return value.slice(0, 3) + "***";
  return value.slice(0, 6) + "*".repeat(value.length - 8) + value.slice(-2);
}

/** Why the sender refuses a picked number: outside DE / AT / CH, or not a mobile range. */
export type SmsNumberRefusal = "country-unsupported" | "not-mobile";

/**
 * The SMS number of one guest, in the sender's order (SMS manual send, plan
 * "Contract and identity", R2-H1 / F3-7):
 *  1. `mobile`, then `phone`; a field that is not a string, or only spaces,
 *     counts as "no value" (never `String(x)` a number or an object);
 *  2. the first value that normalises AND the sender may send to wins;
 *  3. else the first value that normalises at all (its refusal says why the
 *     sender will skip it);
 *  4. else `invalid-phone` when some field had a value, `no-phone` when none.
 * `verdict` is the SMS-check bucket the drawer explains with: `reachable`,
 * `noNumber`, `unreachableNumber`.
 *
 * MIRROR: main-consumer `pickGuestPhone` + `smsAddressRefusal`
 * (`mongo-sms-dispatcher.ts`, the binding one), main-backend
 * `SmsPhone.pickGuest`. Pinned by `src/lib/__fixtures__/phone-pick-parity.json`
 * (byte copy of the consumer's, `smsPhone.pick.parity.test.ts`).
 */
export interface SmsNumberPick {
  pick: "ok" | "no-phone" | "invalid-phone";
  e164: string | null;
  refusal: SmsNumberRefusal | null;
  skip: "no-phone" | "invalid-phone" | "country-unsupported" | null;
  verdict: "reachable" | "noNumber" | "unreachableNumber";
}

function smsNumberRefusal(e164: string): SmsNumberRefusal | null {
  if (!isAllowedSmsCountry(e164)) return "country-unsupported";
  if (!isMobileE164(e164)) return "not-mobile";
  return null;
}

export function pickSmsNumber(mobile: unknown, phone: unknown): SmsNumberPick {
  let sawValue = false;
  let first: string | null = null;
  for (const raw of [mobile, phone]) {
    if (typeof raw !== "string" || raw.trim().length === 0) continue;
    sawValue = true;
    const e164 = normalizeE164(raw);
    if (e164 === null) continue;
    if (smsNumberRefusal(e164) === null) {
      return { pick: "ok", e164, refusal: null, skip: null, verdict: "reachable" };
    }
    first ??= e164;
  }
  if (first !== null) {
    const refusal = smsNumberRefusal(first);
    return {
      pick: "ok",
      e164: first,
      refusal,
      // The sender books a landline as `invalid-phone`, a foreign number as `country-unsupported`.
      skip: refusal === "not-mobile" ? "invalid-phone" : "country-unsupported",
      verdict: "unreachableNumber",
    };
  }
  return sawValue
    ? { pick: "invalid-phone", e164: null, refusal: null, skip: "invalid-phone", verdict: "unreachableNumber" }
    : { pick: "no-phone", e164: null, refusal: null, skip: "no-phone", verdict: "noNumber" };
}
